import * as THREE from 'three';
import type { ReferencePlane } from '../../../../../reference/ReferencePlane';
import type { WorldCoordinate } from '../../../model/ProjectionResult';
import type {
	SupportProjectionPreprocessor,
	ProjectedPlanarBasis,
	DecomposedProjectedEllipse,
} from '../../../../../../types/reference/sketch-entity-reference/SupportProjectionPreprocessor';

export type {
	SupportProjectionPreprocessor,
	ProjectedPlanarBasis,
	DecomposedProjectedEllipse,
} from '../../../../../../types/reference/sketch-entity-reference/SupportProjectionPreprocessor';

/**
 * 投影计算统一公差。
 */
export const PROJECTION_EPSILON = 1e-8;

/**
 * 将向量转为固定长度世界坐标元组。
 *
 * @param vector 三维向量。
 * @returns 世界坐标元组。
 */
export function toWorldCoordinate(vector: THREE.Vector3): WorldCoordinate {
	return [vector.x, vector.y, vector.z];
}

/**
 * 将来源平面基向量映射到目标草图平面的 2D 局部坐标。
 * 说明：
 * 1. 这里构造的二维线性映射是 circle/arc 非平行投影的唯一真相入口；
 * 2. `axisXLocal` / `axisYLocal` 已经乘上对应半径，可直接表示参数方程系数；
 * 3. 任何下游都不应再自行拼接“法向夹角近似”逻辑。
 *
 * @param input 投影基输入。
 * @returns 目标平面局部基向量。
 */
export function buildProjectedPlanarBasis(input: {
	centerWorld: WorldCoordinate;
	planeXAxisWorld: WorldCoordinate;
	planeYAxisWorld: WorldCoordinate;
	radiusX: number;
	radiusY: number;
	targetReferencePlane: ReferencePlane;
}): ProjectedPlanarBasis {
	const targetXAxis = input.targetReferencePlane.xAxis;
	const targetYAxis = input.targetReferencePlane.yAxis;
	const centerWorldVector = new THREE.Vector3(...input.centerWorld);
	const axisXWorld = new THREE.Vector3(...input.planeXAxisWorld).multiplyScalar(input.radiusX);
	const axisYWorld = new THREE.Vector3(...input.planeYAxisWorld).multiplyScalar(input.radiusY);
	const centerLocal = input.targetReferencePlane.worldToLocal(centerWorldVector);

	return {
		centerLocal,
		axisXLocal: new THREE.Vector2(
			axisXWorld.dot(targetXAxis),
			axisXWorld.dot(targetYAxis),
		),
		axisYLocal: new THREE.Vector2(
			axisYWorld.dot(targetXAxis),
			axisYWorld.dot(targetYAxis),
		),
	};
}

/**
 * 将带旋转角的椭圆基准轴映射到目标草图平面。
 * 说明：
 * 1. `SketchEllipseObject` 的旋转角定义在来源草图局部 XY 轴上；
 * 2. 投影前必须先把局部长短轴旋转成世界方向，否则斜向椭圆会丢失姿态；
 * 3. 这里仍复用 `buildProjectedPlanarBasis`，保证圆/弧/椭圆都走同一套线性映射入口。
 *
 * @param input 椭圆投影基输入。
 * @returns 目标平面局部基向量。
 */
export function buildProjectedRotatedEllipseBasis(input: {
	centerWorld: WorldCoordinate;
	planeXAxisWorld: WorldCoordinate;
	planeYAxisWorld: WorldCoordinate;
	radiusX: number;
	radiusY: number;
	rotation: number;
	targetReferencePlane: ReferencePlane;
}): ProjectedPlanarBasis {
	const sourceXAxis = new THREE.Vector3(...input.planeXAxisWorld).normalize();
	const sourceYAxis = new THREE.Vector3(...input.planeYAxisWorld).normalize();
	const cosRotation = Math.cos(input.rotation);
	const sinRotation = Math.sin(input.rotation);
	const majorAxisWorld = sourceXAxis.clone()
		.multiplyScalar(cosRotation)
		.add(sourceYAxis.clone().multiplyScalar(sinRotation))
		.normalize();
	const minorAxisWorld = sourceXAxis.clone()
		.multiplyScalar(-sinRotation)
		.add(sourceYAxis.clone().multiplyScalar(cosRotation))
		.normalize();

	return buildProjectedPlanarBasis({
		centerWorld: input.centerWorld,
		planeXAxisWorld: toWorldCoordinate(majorAxisWorld),
		planeYAxisWorld: toWorldCoordinate(minorAxisWorld),
		radiusX: input.radiusX,
		radiusY: input.radiusY,
		targetReferencePlane: input.targetReferencePlane,
	});
}

/**
 * 对二维线性映射后的椭圆进行标准化分解。
 * 说明：
 * 1. 将 `c + u cos(t) + v sin(t)` 统一分解为目标平面局部椭圆参数；
 * 2. 使用 `A*A^T` 的特征值分解收口，避免手写多套圆/椭圆特殊分支；
 * 3. 当两个奇异值都很小，说明整体退化为点，应由调用方视为失败。
 *
 * @param basis 投影到目标平面的二维基。
 * @returns 标准化椭圆参数；完全退化时返回 null。
 */
export function decomposeProjectedEllipseBasis(
	basis: ProjectedPlanarBasis,
): DecomposedProjectedEllipse | null {
	const u = basis.axisXLocal;
	const v = basis.axisYLocal;
	const m11 = u.x * u.x + v.x * v.x;
	const m12 = u.x * u.y + v.x * v.y;
	const m22 = u.y * u.y + v.y * v.y;
	const trace = m11 + m22;
	const determinant = m11 * m22 - m12 * m12;
	const discriminant = Math.max(0, trace * trace - 4 * determinant);
	const sqrtDiscriminant = Math.sqrt(discriminant);
	const lambda1 = Math.max(0, (trace + sqrtDiscriminant) * 0.5);
	const lambda2 = Math.max(0, (trace - sqrtDiscriminant) * 0.5);
	const radiusX = Math.sqrt(lambda1);
	const radiusY = Math.sqrt(lambda2);

	if (radiusX <= PROJECTION_EPSILON) {
		return null;
	}

	const majorAxisUnitLocal = resolveEigenvector(m11, m12, m22, lambda1);
	const minorAxisUnitLocal = new THREE.Vector2(
		-majorAxisUnitLocal.y,
		majorAxisUnitLocal.x,
	);

	return {
		centerLocal: basis.centerLocal.clone(),
		radiusX,
		radiusY,
		majorAxisUnitLocal,
		minorAxisUnitLocal,
		rotation: Math.atan2(majorAxisUnitLocal.y, majorAxisUnitLocal.x),
	};
}

/**
 * 计算目标平面局部坐标中的椭圆参数点。
 *
 * @param basis 投影基。
 * @param parameter 源曲线参数角。
 * @returns 目标平面局部点。
 */
export function resolveProjectedLocalPointByParameter(
	basis: ProjectedPlanarBasis,
	parameter: number,
): THREE.Vector2 {
	return basis.centerLocal.clone()
		.addScaledVector(basis.axisXLocal, Math.cos(parameter))
		.addScaledVector(basis.axisYLocal, Math.sin(parameter));
}

/**
 * 将目标平面局部二维点写回世界坐标元组。
 *
 * @param localPoint 局部点。
 * @param targetReferencePlane 目标草图平面。
 * @returns 世界坐标元组。
 */
export function localPointToWorldCoordinate(
	localPoint: THREE.Vector2,
	targetReferencePlane: ReferencePlane,
): WorldCoordinate {
	return toWorldCoordinate(targetReferencePlane.localToWorld(localPoint));
}

/**
 * 将目标平面局部点反解为标准椭圆参数角。
 * 说明：
 * 1. 先将点去中心、再逆旋转到椭圆局部坐标；
 * 2. 使用 `atan2(y / radiusY, x / radiusX)` 反解参数角；
 * 3. 当半径退化时返回 null，由调用方决定是否降级。
 *
 * @param localPoint 目标平面局部点。
 * @param ellipse 椭圆分解结果。
 * @returns 椭圆参数角；退化时返回 null。
 */
export function resolveEllipseParameterAngle(
	localPoint: THREE.Vector2,
	ellipse: DecomposedProjectedEllipse,
): number | null {
	if (ellipse.radiusX <= PROJECTION_EPSILON || ellipse.radiusY <= PROJECTION_EPSILON) {
		return null;
	}

	const relative = localPoint.clone().sub(ellipse.centerLocal);
	const localX = relative.dot(ellipse.majorAxisUnitLocal);
	const localY = relative.dot(ellipse.minorAxisUnitLocal);
	return Math.atan2(localY / ellipse.radiusY, localX / ellipse.radiusX);
}

/**
 * 判断分解结果是否可视为圆。
 *
 * @param ellipse 椭圆分解结果。
 * @returns 长短半轴足够接近时返回 true。
 */
export function isProjectedCircle(ellipse: DecomposedProjectedEllipse): boolean {
	return Math.abs(ellipse.radiusX - ellipse.radiusY) <= PROJECTION_EPSILON
		* Math.max(1, ellipse.radiusX, ellipse.radiusY);
}

/**
 * 判断分解结果是否已退化为线。
 *
 * @param ellipse 椭圆分解结果。
 * @returns 短轴接近 0 时返回 true。
 */
export function isProjectedLine(ellipse: DecomposedProjectedEllipse): boolean {
	return ellipse.radiusY <= PROJECTION_EPSILON;
}

/**
 * 求二维对称矩阵的单位特征向量。
 *
 * @param m11 矩阵左上元素。
 * @param m12 矩阵非对角元素。
 * @param m22 矩阵右下元素。
 * @param eigenvalue 对应特征值。
 * @returns 单位化特征向量。
 */
function resolveEigenvector(
	m11: number,
	m12: number,
	m22: number,
	eigenvalue: number,
): THREE.Vector2 {
	const vector = Math.abs(m12) > PROJECTION_EPSILON
		? new THREE.Vector2(m12, eigenvalue - m11)
		: Math.abs(m11 - eigenvalue) <= Math.abs(m22 - eigenvalue)
			? new THREE.Vector2(1, 0)
			: new THREE.Vector2(0, 1);
	if (vector.lengthSq() <= PROJECTION_EPSILON) {
		return new THREE.Vector2(1, 0);
	}
	return vector.normalize();
}
